import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { ResidentialService } from '../../core/services/residential.service';
import { ToastrService } from 'ngx-toastr';
import { GuaranteeFormUtils } from '../../core/utils/guarantee.utils';
import { TranslateService } from '@ngx-translate/core';
import { PaymentMsiService } from './payment-msi.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { PaymentMsiAddress } from '../shared-components.model';

const DomUtils = {
  getEl: function getEl(selector) {
    return window.document.querySelector(selector);
  },
  hasClass: function hasClass(el, cssClass) {
    if (el.classList) {
      return el.classList.contains(cssClass);
    }

    return !!el.className.match(new RegExp('(\\s|^)'.concat(cssClass, '(\\s|$)')));
  },
  removeClass: function removeClass(el, cssClass) {
    if (el.classList) {
      el.classList.remove(cssClass);
    } else if (DomUtils.hasClass(el, cssClass)) {
      const reg = new RegExp('(\\s|^)'.concat(cssClass, '(\\s|$)'));
      el.className = el.className.replace(reg, ' ');
    }
  }
};
@UntilDestroy()
@Component({
    selector: 'app-payment-new-carrier',
    templateUrl: './payment-msi.component.html',
    styleUrls: ['./payment-msi.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PaymentMsiComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() data;
  @Input() address: PaymentMsiAddress;
  @Input() loading!: boolean;
  @Input() hasBackButton!: boolean;
  @Input() agreeWithDisclosureParent!: boolean;
  @Input() subjectFormSubmit!: Observable<unknown>;
  @Output() cardStart = new EventEmitter();
  @Output() cardSuccess = new EventEmitter();
  @Output() cardError = new EventEmitter();
  @Output() declinePayment = new EventEmitter();
  @Output() openDisclosure =  new EventEmitter();
  agreeWithDisclosure = false;
  paymentForm;
  state!: string;
  address1;
  address2;
  city!: string;
  zip!: string;
  stateList = GuaranteeFormUtils.STATE_LIST;
  errorMessages: Record<string, any> = {};
  formSent: boolean = false;
  mailingAddress: boolean = true;
  constructor(private resService: ResidentialService,
              private toastrService: ToastrService,
              private translate: TranslateService,
              private paymentMsiService: PaymentMsiService) {
  }
  hooks = {
    // eslint-disable-next-line @typescript-eslint/ban-types
    preFlowHook: (callback: Function) => {
        if (!this.mailingAddress) {
            if (!this.address1 || !this.state || !this.city || !this.zip) {
                this.toastrService.error(this.translate.instant('form.form_validation_error'));
                this.cardError.emit();
            } else {
                this.resService.getExternalPaymentAuth(this.data.quote.id).subscribe((response) => {
                    callback(response);
                });
            }
        } else {
             this.resService.getExternalPaymentAuth(this.data.quote.id).subscribe((response) => {
                 callback(response);
             });
        }
    },
    // eslint-disable-next-line @typescript-eslint/ban-types
    submitFormHook: (callbackFn: Function) => {
        let body;

        if (this.mailingAddress) {
            body = {
                address1: this.address.street_name,
                region: this.address.state,
                postalCode: this.address.zip_code,
                city: this.address.city
            };

            if (this.address.street_two) {
              // eslint-disable-next-line
                (body as any).address2 = this.address.address_two;
            }
        } else {
            body = {
                address1: this.address1,
                region: this.state,
                postalCode: this.zip,
                city: this.city
            };

            if (this.address2) {
              // eslint-disable-next-line
              (body as any).address2 = this.address2;
            }
        }

        callbackFn(body);
    },
  };
  config = this.paymentMsiService.getConfig();

  onCreate = (paymentForm) => {
    this.paymentForm  = paymentForm;
    const onSuccess = (clientToken: string) => {
      this.resService.getPayeezyToken(clientToken).subscribe(
        (value) => {
          this.cardSuccess.emit(value);
        }, () => {
          this.cardError.emit();
        });
    };

    const onError = () => {
      const fields = Object.values<{ field: string; validity: { valid: boolean; empty: boolean } }>(this.paymentForm.state.fields);
      const errorFields = fields.filter(f => f && !f.validity.valid) ;
      this.errorMessages = errorFields.reduce((acc, val) => {
        const errorMessage = val.validity.empty ? `${this.translate.instant('msi_error.the')} ${val.field} ${this.translate.instant('msi_error.field_is_required')}.` :
          `${this.translate.instant('msi_error.the')} ${val.field} ${this.translate.instant('msi_error.field_must_be_valid')}`;
        return {...acc, [val.field]: errorMessage};
      }, {});
      this.toastrService.error(this.translate.instant('form.form_validation_error'));
      this.cardError.emit();
    };
    const form = document.getElementById('firstdataForm');
    form?.addEventListener('submit', (e) => {
      this.formSent = true;
      e.preventDefault();
      this.cardStart.emit();
      paymentForm?.onSubmit(onSuccess, onError);
    });

    paymentForm.on('change', () => {
      this.formSent = false;
    });

    const ccFields = window.document.getElementsByClassName('payment-fields');
    for (const field of ccFields as unknown as HTMLCollectionOf<Element>[]) {
      DomUtils.removeClass(field, 'disabled');
    }

    this.subjectFormSubmit.subscribe(() => {
      this.formSent = true;
      this.cardStart.emit();
      paymentForm.onSubmit(onSuccess, onError);
    });
  }

  ngAfterViewInit(): void {
    this.initPaymentForm();
  }

  initPaymentForm() {
    this.config = this.paymentMsiService.getConfig();
    (window as unknown as { firstdata: { createPaymentForm: (config: unknown, hooks: unknown, onCreate: (paymentForm: unknown) => void) => void } }).firstdata.createPaymentForm(this.config, this.hooks, this.onCreate);
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['agreeWithDisclosureParent']) {
          this.agreeWithDisclosure = changes['agreeWithDisclosureParent']?.currentValue;
      }
  }

  decline($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.declinePayment.emit();
  }

  ngOnDestroy(): void {
    if (this.paymentForm) {
      this.paymentForm.destroyFields();
    }
  }
}
